import { resolveSpxValue } from '@wix/thunderbolt-commons'
import type { Spx, VariableReference } from '@wix/thunderbolt-becky-types'
import type { StylePropertiesTypes } from '@wix/document-services-types'

/** Internal Converters */
const keywordConverter = (prop: StylePropertiesTypes.Keyword<string>): string => prop.value

const keywordsConverter = (prop: StylePropertiesTypes.Keywords<StylePropertiesTypes.Keyword<string>>) =>
	prop.value.map((keyword) => keywordConverter(keyword)).join(' ')

const numberConverter = (prop: StylePropertiesTypes.Number): string => `${prop.value}`

const percentageConverter = (prop: StylePropertiesTypes.Percentage): string => `${prop.value}%`

const spxConverter = (prop: StylePropertiesTypes.Length, spx?: Spx): string => resolveSpxValue(`${prop.value}spx`, spx)

const commonAngleConverter = (prop: StylePropertiesTypes.Angle): string => `${prop.value}${prop.unit}`

const commonLengthConverter = (prop: StylePropertiesTypes.Length): string => `${prop.value}${prop.unit}`

const lengthConverter = (prop: StylePropertiesTypes.Length, spx?: Spx): string =>
	lengthTypeConverter[prop.unit](prop, spx)

const angleConverter = (prop: StylePropertiesTypes.Angle): string => angleTypeToConverter[prop.unit](prop)

const urlConverter = (prop: StylePropertiesTypes.URL): string => `url(${prop.value})`

const stringConverter = (prop: StylePropertiesTypes.String): string => prop.value

const variableConverter = (prop: VariableReference): string => {
	const variableName = prop.shouldRenderVariableName ? prop.variableName : prop.variableId.replace('#', '')
	return `var(--${variableName})`
}

/** Internal Maps */
const lengthTypeConverter: Record<
	StylePropertiesTypes.LengthUnit[number],
	typeof spxConverter | typeof commonLengthConverter
> = {
	spx: spxConverter,
	px: commonLengthConverter,
	pt: commonLengthConverter,
	pc: commonLengthConverter,
	in: commonLengthConverter,
	cm: commonLengthConverter,
	mm: commonLengthConverter,
	em: commonLengthConverter,
	rem: commonLengthConverter,
	vw: commonLengthConverter,
	vh: commonLengthConverter,
	vmin: commonLengthConverter,
	vmax: commonLengthConverter,
	ex: commonLengthConverter,
	ic: commonLengthConverter,
	rcap: commonLengthConverter,
	rch: commonLengthConverter,
	rex: commonLengthConverter,
	ric: commonLengthConverter,
	rlh: commonLengthConverter,
	vb: commonLengthConverter,
	vi: commonLengthConverter,
	dvb: commonLengthConverter,
	dvh: commonLengthConverter,
	dvi: commonLengthConverter,
	dvmax: commonLengthConverter,
	dvmin: commonLengthConverter,
	dvw: commonLengthConverter,
	lvb: commonLengthConverter,
	lvh: commonLengthConverter,
	lvi: commonLengthConverter,
	lvmax: commonLengthConverter,
	lvmin: commonLengthConverter,
	lvw: commonLengthConverter,
	Q: commonLengthConverter,
	ch: commonLengthConverter,
	cap: commonLengthConverter,
	cqw: commonLengthConverter,
	cqh: commonLengthConverter,
	cqi: commonLengthConverter,
	cqb: commonLengthConverter,
	cqmin: commonLengthConverter,
	cqmax: commonLengthConverter,
	lh: commonLengthConverter,
	svb: commonLengthConverter,
	svh: commonLengthConverter,
	svi: commonLengthConverter,
	svmax: commonLengthConverter,
	svmin: commonLengthConverter,
	svw: commonLengthConverter,
}

const angleTypeToConverter = {
	deg: commonAngleConverter,
	rad: commonAngleConverter,
	grad: commonAngleConverter,
	turn: commonAngleConverter,
}

type AtomicStyleProps =
	| StylePropertiesTypes.Number
	| StylePropertiesTypes.Keyword<string>
	| StylePropertiesTypes.Keywords<StylePropertiesTypes.Keyword<string>>
	| StylePropertiesTypes.Percentage
	| StylePropertiesTypes.Length
	| StylePropertiesTypes.Angle
	| StylePropertiesTypes.URL
	| StylePropertiesTypes.String
	| VariableReference

/** External Map */
export const atomicStylePropsConverterMap = {
	Number: numberConverter,
	Keyword: keywordConverter,
	Keywords: keywordsConverter,
	Percentage: percentageConverter,
	Length: lengthConverter,
	Angle: angleConverter,
	Url: urlConverter,
	String: stringConverter,
	VariableReference: variableConverter,
} as Record<AtomicStyleProps['type'], (prop: AtomicStyleProps, spx?: Spx) => string>
